import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Alert,
} from '@mui/material';
import { ArrowForward, CalendarToday } from '@mui/icons-material';
import { format } from 'date-fns';
import { getProjectDate, advanceProjectDate, AdvanceDateResponse, ProjectApiError } from '../../services/projectApi';
import { useAuth } from '../../contexts/AuthContext';

interface ProjectDateControlsProps {
  projectId: number;
  initialProjectDate?: string | null; // Initial project date from execution plan (lock date)
  onDateChange?: (newDate: string) => void;
  onActionsTriggered?: (result: AdvanceDateResponse) => void;
}

export const ProjectDateControls: React.FC<ProjectDateControlsProps> = ({
  projectId,
  initialProjectDate,
  onDateChange,
  onActionsTriggered,
}) => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [projectDate, setProjectDate] = useState<string | null>(initialProjectDate || null);
  const [timezone, setTimezone] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(!initialProjectDate); // Only load if no initial date provided
  const [isAdvancing, setIsAdvancing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Update project date when initialProjectDate prop changes
  useEffect(() => {
    if (initialProjectDate) {
      console.log('ProjectDateControls: initialProjectDate prop changed:', {
        initialProjectDate,
        type: typeof initialProjectDate,
      });
      setProjectDate(initialProjectDate);
      // Don't call onDateChange here - it will cause a loop
      // The parent already has the correct date
    }
  }, [initialProjectDate]);


  // Load project date on mount and when projectId changes (only if no initial date provided)
  useEffect(() => {
    if (!projectId || initialProjectDate) return; // Skip if initial date is provided

    const loadProjectDate = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await getProjectDate(projectId);
        console.log('ProjectDateControls: getProjectDate API response:', {
          projectDate: response.projectDate,
          timezone: response.timezone,
        });
        setProjectDate(response.projectDate);
        setTimezone(response.timezone || null);
        if (onDateChange) {
          onDateChange(response.projectDate);
        }
      } catch (err) {
        const apiError = err as ProjectApiError;
        setError(apiError.message || 'Failed to load project date');
        console.error('Error loading project date:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadProjectDate();
  }, [projectId, initialProjectDate, onDateChange]);

  const handleAdvanceDay = async (retryCount = 0) => {
    if (!projectId || isAdvancing) return;

    // Check authentication before starting
    if (!isAuthenticated || authLoading) {
      const error = { error: true, message: 'Authentication required. Please wait for authentication to complete.', code: 'AUTH_REQUIRED' } as ProjectApiError;
      setError(error.message);
      return;
    }

    setIsAdvancing(true);
    // Clear error on retry
    if (retryCount > 0) {
      setError(null);
    }

    try {
      const result = await advanceProjectDate(projectId);
      console.log('ProjectDateControls: Advance date result:', {
        newProjectDate: result.newProjectDate,
        actionsTriggeredCount: result.actionsTriggeredCount,
        actionsTriggered: result.actionsTriggered,
      });
      
      setProjectDate(result.newProjectDate);
      if (onDateChange) {
        onDateChange(result.newProjectDate);
      }
      if (onActionsTriggered) {
        onActionsTriggered(result);
      }
      setError(null); // Clear any previous errors on success
      setIsAdvancing(false); // Clear loading state on success
    } catch (err) {
      const apiError = err as ProjectApiError;
      
      // Handle authentication errors with retry logic
      if (apiError.code === 'AUTH_REQUIRED') {
        // If auth is required but we think we're authenticated, wait and retry
        // This handles the race condition where cookies aren't set yet
        if (retryCount < 2 && isAuthenticated) {
          const delay = Math.pow(2, retryCount) * 500; // 500ms, 1000ms
          console.warn(`ProjectDateControls: Auth not ready, retrying advance day in ${delay}ms (attempt ${retryCount + 1}/2)`);
          setTimeout(() => {
            handleAdvanceDay(retryCount + 1);
          }, delay);
          // Keep loading state true during retry
          return;
        } else {
          setError('Authentication required. Please refresh the page and try again.');
          setIsAdvancing(false);
        }
      } else if (apiError.code === 'NETWORK_ERROR') {
        // Network errors - retry with exponential backoff
        if (retryCount < 2) {
          const delay = Math.pow(2, retryCount) * 1000; // 1s, 2s
          console.warn(`ProjectDateControls: Network error, retrying advance day in ${delay}ms (attempt ${retryCount + 1}/2):`, err);
          setTimeout(() => {
            handleAdvanceDay(retryCount + 1);
          }, delay);
          // Keep loading state true during retry
          return;
        } else {
          // Max retries reached
          const errorMessage = apiError.message || 'Failed to advance project date';
          setError(errorMessage);
          console.error('Error advancing project date after retries:', {
            error: err,
            apiError,
            message: errorMessage,
            code: apiError.code,
          });
          
          // Show more detailed error in console for debugging
          console.error('Network error details:', {
            apiBaseUrl: import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000',
            projectId,
          });
          setIsAdvancing(false);
        }
      } else {
        // Other errors (validation, not found, etc.) - don't retry
        const errorMessage = apiError.message || 'Failed to advance project date';
        setError(errorMessage);
        console.error('Error advancing project date:', {
          error: err,
          apiError,
          message: errorMessage,
          code: apiError.code,
        });
        setIsAdvancing(false);
      }
    }
  };

  // Format timezone for display (e.g., "America/New_York" -> "EST" or "EDT")
  const formatTimezone = (tz: string | null): string => {
    if (!tz) return '';
    
    // Try to get a short timezone name
    try {
      const date = projectDate ? new Date(projectDate) : new Date();
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        timeZoneName: 'short',
      });
      const parts = formatter.formatToParts(date);
      const tzName = parts.find(part => part.type === 'timeZoneName')?.value || '';
      return tzName ? ` (${tzName})` : ` (${tz})`;
    } catch {
      return ` (${tz})`;
    }
  };

  // Format date for display - format directly from date components to avoid timezone shifts
  // This completely avoids Date object creation and date-fns formatting that might cause timezone issues
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'Not set';
    try {
      // Parse date string (YYYY-MM-DD) and format manually from components
      const dateOnly = dateString.split('T')[0];
      const [year, month, day] = dateOnly.split('-').map(Number);
      console.log('ProjectDateControls formatDate:', {
        input: dateString,
        dateOnly,
        year,
        month,
        day,
        parsed: !isNaN(year) && !isNaN(month) && !isNaN(day),
      });
      if (isNaN(year) || isNaN(month) || isNaN(day)) {
        // Fallback to standard Date parsing if format is unexpected
        const date = new Date(dateString);
        const formatted = format(date, 'MMMM dd, yyyy');
        console.log('ProjectDateControls formatDate (fallback):', {
          input: dateString,
          parsedDate: date,
          formatted,
        });
        return formatted;
      }
      // Format manually from components to completely avoid timezone issues
      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      const monthName = monthNames[month - 1]; // month is 1-indexed from input
      const formatted = `${monthName} ${day}, ${year}`;
      console.log('ProjectDateControls formatDate (manual):', {
        input: dateString,
        year,
        month,
        day,
        formatted,
      });
      return formatted;
    } catch (error) {
      console.error('ProjectDateControls formatDate error:', error, dateString);
      return dateString;
    }
  };

  if (isLoading) {
    return (
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body1">Loading project date...</Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
          <CalendarToday fontSize="small" color="action" />
          <Typography variant="body1" component="div">
            <strong>Project Date:</strong> {formatDate(projectDate)}
            {timezone && formatTimezone(timezone)}
          </Typography>
        </Box>
        
        {timezone && (
          <Typography variant="body2" color="text.secondary">
            Actions trigger at 8:00 AM{formatTimezone(timezone)}
          </Typography>
        )}

        <Button
          variant="contained"
          startIcon={isAdvancing ? <CircularProgress size={16} /> : <ArrowForward />}
          onClick={() => handleAdvanceDay()}
          disabled={isAdvancing || !projectDate}
          sx={{ minWidth: 140 }}
        >
          {isAdvancing ? 'Advancing...' : 'Advance Day'}
        </Button>
      </Box>
    </Paper>
  );
};

